import { getUrlParameter } from './window';
import { getObj } from './localStorage';

let Sentry;
let BrowserTracing;
const ensureSentryLoaded = async () => {
  if (!Sentry) {
    const mod = await import(/* webpackChunkName: "sentry" */ '@sentry/browser');
    Sentry = mod;
    const tracing = await import(/* webpackChunkName: "sentry" */ '@sentry/tracing');
    BrowserTracing = tracing.BrowserTracing;
  }
};

const { version } = require('../package.json');

const environment = getObj('env').env || 'production';
const sentryEnabled =
  ['production', 'prod', 'local', 'localProd', 'dev'].includes(environment) ||
  getUrlParameter('sentry') === '1';

const getSessionData = (callback) => {
  // Our GA4 cookie ID
  const pattern = /(?:_ga_MR0R2ZKP5N=GS|_ga_BP26YPEPJT=GS)\d\.\d\.(.+?)(?:;|$)/;
  const match = document.cookie.match(pattern);
  const parts = match?.[1].split('.');

  if (!parts) {
    // Cookie not yet available; wait a bit and try again.
    window.setTimeout(() => getSessionData(callback), 200);
    return;
  }

  callback({
    ga_session_id: parts.shift(),
    ga_session_number: parts.shift(),
  });
};

class CustomError extends Error {
  constructor(message, level = 'error', code = null) {
    super(message);
    this.name = this.constructor.name;
    this.level = level;
    // Optional machine-readable code (e.g. 'coupon_not_found' from verifyCoupon)
    if (code) this.code = code;
  }
}

export { CustomError };

export const logError = (message, contexts) => {
  console.error(message, { ...contexts });

  if (!sentryEnabled) return;

  const formattedMessage = (() => {
    if (message instanceof Error) return message;
    if (typeof message === 'string') return new Error(message);
    // Cross-realm / duck-typed Error (e.g. thrown from an Adyen or Google Maps
    // iframe): fails `instanceof Error` but still carries message + stack.
    // Preserve them so the report doesn't collapse into "[object Object]".
    if (message && typeof message === 'object' && typeof message.message === 'string') {
      const recovered = new Error(message.message);
      if (message.stack) recovered.stack = message.stack;
      if (message.name) recovered.name = message.name;
      return recovered;
    }
    try {
      return new Error(`NonError: ${JSON.stringify(message)}`);
    } catch (_) {
      return new Error('NonError: [unserializable object]');
    }
  })();

  if (formattedMessage instanceof CustomError && formattedMessage.level === 'warning') {
    console.log('Warning:', formattedMessage.message, { ...contexts });
    return;
  }
  ensureSentryLoaded()
    .then(() => {
      Sentry.withScope((scope) => {
        if (contexts) {
          Object.entries(contexts).forEach(([key, value]) => scope.setExtra(key, value));
        }
        if (message && typeof message === 'object' && !(message instanceof Error)) {
          scope.setExtra('raw', message);
        }
        Sentry.captureException(formattedMessage);
      });
    })
    .catch((error) => {
      console.error('[Sentry] Failed to log error:', error);
    });
};

export const startReplay = () => {
  try {
    if (environment !== 'production') return;
    ensureSentryLoaded().then(() => {
      const client = Sentry.getCurrentHub().getClient();
      const options = client.getOptions();
      if (options.replaysSessionSampleRate === 1) return;
      options.replaysSessionSampleRate = 1;
      console.log('Started Replay');
    });
  } catch (error) {
    console.log('Failed to start Replay', error);
  }
};

export const initSentry = () => {
  ensureSentryLoaded()
    .then(() => {
      const integrations = [new BrowserTracing()];
      if (environment === 'production') {
        integrations.push(
          new Sentry.Replay({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [window.location.origin, '*.klikkie.com'],
          })
        );
      }
      Sentry.init({
        dsn: 'https://bf673c7ac2fb48fbbe17a00021ea58c9@o262013.ingest.us.sentry.io/4504112614408192',
        release: `klikkie-webflow-js@${version}`,
        integrations,
        environment,
        // tunnel: 'https://errors.klikkie.com/', // TODO tunnel is not working yet
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        tracesSampleRate: 1.0,
        denyUrls: ['adyen.com'],
      });
      getSessionData((sessionData) => {
        Sentry.setContext('ga', sessionData);
      });
      console.log('[Sentry] Initialized successfully', {
        environment,
        release: `klikkie-webflow-js@${version}`,
      });
    })
    .catch((error) => {
      console.error('[Sentry] Failed to initialize:', error);
    });
};

window.addEventListener('error', (event) => {
  // Prefer the real thrown Error — it carries the actual message + stack.
  // Falling back to a synthetic Error('An unhandled error occurred:') (the old
  // behaviour) discarded both and collapsed every top-level error into one
  // useless fingerprint.
  const realError = event.error instanceof Error ? event.error : new Error(event.message || 'Unhandled error');
  logError(realError, {
    source: event.filename,
    line: event.lineno,
    col: event.colno,
    rawMessage: event.message,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const realError = reason instanceof Error ? reason : new Error(`Unhandled promise rejection: ${typeof reason === 'string' ? reason : JSON.stringify(reason)}`);
  logError(realError, { reason });
});

// Expose test function globally for debugging
window.testSentry = async () => {
  console.log('[Sentry Test] Environment:', environment);
  console.log('[Sentry Test] Sentry enabled:', sentryEnabled);
  console.log('[Sentry Test] Version:', version);

  if (!sentryEnabled) {
    console.error('[Sentry Test] Sentry is DISABLED. Environment:', environment);
    return 'Sentry is disabled';
  }

  try {
    await ensureSentryLoaded();
    console.log('[Sentry Test] Sentry module loaded:', !!Sentry);
    console.log('[Sentry Test] Sentry.captureException exists:', typeof Sentry.captureException);

    const client = Sentry.getCurrentHub().getClient();
    console.log('[Sentry Test] Sentry client:', !!client);
    console.log('[Sentry Test] DSN:', client?.getDsn?.()?.toString?.() || 'N/A');

    const eventId = Sentry.captureException(new Error(`SENTRY TEST - ${new Date().toISOString()}`));
    console.log('[Sentry Test] Event ID:', eventId);

    // Force flush to ensure event is sent
    await Sentry.flush(5000);
    console.log('[Sentry Test] Flushed!');

    return `Test error sent with ID: ${eventId}. Check Sentry dashboard.`;
  } catch (error) {
    console.error('[Sentry Test] Failed:', error);
    return `Failed: ${error.message}`;
  }
};
